import React, { useEffect, useMemo, useState } from 'react';
import SiteFooter from './components/SiteFooter';
import SiteHeader from './components/SiteHeader';
import AuthPage from './pages/AuthPage';
import DashboardPage from './pages/DashboardPage';
import LandingPage from './pages/LandingPage';

const TOKEN_KEY = 'postcard_saas_token';
const USER_KEY = 'postcard_saas_user';
const THEME_KEY = 'postcard_saas_theme';

const getStoredUser = () => {
  const raw = localStorage.getItem(USER_KEY);
  if (!raw) return null;

  try {
    return JSON.parse(raw);
  } catch (_error) {
    return null;
  }
};

const getInitialTheme = () => {
  const storedTheme = localStorage.getItem(THEME_KEY);
  if (storedTheme === 'dark' || storedTheme === 'light') {
    return storedTheme;
  }

  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
};

function App() {
  const [token, setToken] = useState(localStorage.getItem(TOKEN_KEY) || '');
  const [user, setUser] = useState(getStoredUser());
  const [authMode, setAuthMode] = useState('login');
  const [view, setView] = useState(() => (localStorage.getItem(TOKEN_KEY) ? 'dashboard' : 'landing'));
  const [theme, setTheme] = useState(getInitialTheme);
  const [pendingMenuKey, setPendingMenuKey] = useState('');

  const isAuthenticated = useMemo(() => Boolean(token), [token]);
  const isDark = theme === 'dark';
  const isAuthView = !isAuthenticated && view === 'auth';

  useEffect(() => {
    if (view !== 'landing' || !pendingMenuKey) return;

    const timer = window.setTimeout(() => {
      const target = document.getElementById(pendingMenuKey);
      if (target) {
        target.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
      setPendingMenuKey('');
    }, 60);

    return () => window.clearTimeout(timer);
  }, [view, pendingMenuKey]);

  const onAuthSuccess = (payload) => {
    localStorage.setItem(TOKEN_KEY, payload.token);
    localStorage.setItem('token', payload.token);
    localStorage.setItem(USER_KEY, JSON.stringify(payload.user));

    setToken(payload.token);
    setUser(payload.user);
    setView('dashboard');
  };

  const onLogout = () => {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem('token');
    localStorage.removeItem(USER_KEY);

    setToken('');
    setUser(null);
    setView('landing');
  };

  const onOpenAuth = (mode) => {
    setAuthMode(mode);
    setView('auth');
  };

  const onToggleTheme = () => {
    setTheme((prev) => {
      const next = prev === 'dark' ? 'light' : 'dark';
      localStorage.setItem(THEME_KEY, next);
      return next;
    });
  };

  const onOpenLanding = () => {
    setPendingMenuKey('');
    setView('landing');
  };

  const onOpenDashboard = () => setView(isAuthenticated ? 'dashboard' : 'auth');

  const onMenuAction = (menuKey) => {
    if (view !== 'landing') {
      setPendingMenuKey(menuKey);
      setView('landing');
      return;
    }

    const target = document.getElementById(menuKey);
    if (target) {
      target.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  return (
    <main
      className={`relative min-h-screen overflow-x-hidden p-4 transition-colors duration-500 md:p-6 ${
        isDark ? 'bg-slate-950 text-slate-100' : 'bg-slate-50 text-slate-900'
      }`}
    >
      <div className="pointer-events-none absolute inset-0 overflow-hidden">
        <div
          className={`absolute -left-24 top-8 h-72 w-72 rounded-full blur-3xl ${
            isDark ? 'bg-blue-700/30' : 'bg-cyan-300/40'
          } animate-blob`}
        />
        <div
          className={`absolute -right-20 top-32 h-64 w-64 rounded-full blur-3xl ${
            isDark ? 'bg-amber-500/20' : 'bg-orange-300/35'
          } animate-blob-delayed`}
        />
      </div>

      {!isAuthView && (
        <div className="fixed inset-x-0 top-0 z-[90] px-4 pt-3 md:px-6">
          <div className="mx-auto max-w-7xl">
            <SiteHeader
              isDark={isDark}
              isAuthenticated={isAuthenticated}
              user={user}
              onToggleTheme={onToggleTheme}
              onOpenAuth={onOpenAuth}
              onOpenLanding={onOpenLanding}
              onOpenDashboard={onOpenDashboard}
              onLogout={onLogout}
              onMenuAction={onMenuAction}
            />
          </div>
        </div>
      )}

      <section className={`relative mx-auto max-w-7xl ${isAuthView ? '' : 'pt-[108px] md:pt-[112px] lg:pt-[150px]'}`}>
        {isAuthenticated ? (
          <DashboardPage token={token} user={user} isDark={isDark} onLogout={onLogout} onToggleTheme={onToggleTheme} />
        ) : view === 'landing' ? (
          <LandingPage isDark={isDark} />
        ) : (
          <AuthPage
            isDark={isDark}
            initialMode={authMode}
            onBack={onOpenLanding}
            onAuthSuccess={onAuthSuccess}
          />
        )}

        {!isAuthView && <SiteFooter isDark={isDark} />}
      </section>
    </main>
  );
}

export default App;
